import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminService } from '../../services';
import { useUser } from '../../context/UserContext';
import {
  AdminModal, AdminPageHeader, AdminTooltip, Avatar, Btn, Card, CheckBox, DataTable, DrillList, EmptyLine, Field, FieldLabel, Loading, Pill,
  SearchField, SegTrack, StatTile, SubTabs, TileGrid, inputClass, money, shortDate, useAdminShell,
} from '../../components/admin/shell';
import { BarList, ColumnChart, LineChart, fmt } from '../../components/admin/analytics/charts';
import WalletMoveModal from '../../components/admin/WalletMoveModal';

const RANGES = [7, 30, 90];
const LIVE = new Set(['selling', 'funding', 'greenlit', 'processing_greenlight']);

const moneyAxis = (cents) => {
  const d = Math.abs(cents || 0) / 100;
  if (d >= 1_000_000) return `$${(d / 1_000_000).toFixed(1)}M`;
  if (d >= 1000) return `$${(d / 1000).toFixed(d >= 10_000 ? 0 : 1)}K`;
  return `$${Math.round(d)}`;
};
const shortDay = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(iso);
  return `${d.toLocaleString('en', { month: 'short' }).toUpperCase()} ${d.getDate()}`;
};
const statusWord = (s) => String(s || '').replace(/_/g, ' ').toUpperCase();
const statusTone = (s) => (LIVE.has(s) ? 'good' : s === 'canceled' || s === 'dissolved' ? 'bad' : 'neutral');

/** How this host's escrow is released: INSTANT, the default 24 H, or their own delay. */
export const releaseWord = (h) => (h.instantEscrow ? 'INSTANT' : h.escrowReleaseHours == null ? '24 H' : h.escrowReleaseHours === 0 ? 'AT THE END' : `${h.escrowReleaseHours} H`);
const releaseTone = (h) => (h.instantEscrow ? 'magenta' : h.escrowReleaseHours == null ? 'neutral' : 'dark');

/**
 * PEOPLE › HOSTS — everyone who has published an event, in the USERS table's format, with
 * the hosts-only tiles and charts above it. Per row: the dropdown (wallet + hosting record +
 * what is held, from `GET api/admin/hosts/{id}`), ESCROW SETTINGS (`hosts · edit`: INSTANT
 * ESCROW and the RELEASE DELAY) and RELEASE NOW (`hosts · release`: the step log of what
 * moved). ADJUST WALLET (`wallets · adjust`) and TRANSFER WALLET (superadmin) open the shared
 * wallet modal. Every row action sits behind the row's ⋯ (Oct 28 2026). A row click opens the
 * person drawer, like USERS.
 */
export default function HostsPage() {
  const { can, isSuperadmin } = useUser();
  const { openPerson, canSeePeople } = useAdminShell();
  const [params, setParams] = useSearchParams();
  const canEdit = can('hosts', 'edit');
  const canRelease = can('hosts', 'release');
  const canAdjust = can('wallets', 'adjust');
  const [search, setSearch] = useState(params.get('search') || '');
  const [page, setPage] = useState(1);
  const tab = params.get('tab') || 'all';
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [drill, setDrill] = useState(null);
  const [open, setOpen] = useState({});          // userId → true while the dropdown is out
  const [details, setDetails] = useState({});    // userId → detail payload | 'loading' | 'error'
  const [settingsFor, setSettingsFor] = useState(null);
  const [releaseFor, setReleaseFor] = useState(null);
  const [walletFor, setWalletFor] = useState(null);   // { person, mode } while the wallet modal is out
  const walletPerson = (h) => ({ id: h.id, name: h.displayName || h.email, email: h.email, handle: h.handle, avatarUrl: h.avatarUrl, balance: h.walletBalance || 0, escrow: h.escrowBalance || 0 });

  const applied = params.get('search') || '';
  const load = useCallback(async () => {
    setLoading(true);
    const r = await adminService.getHosts(page, applied, tab);
    setLoading(false);
    if (r.success) { setRows(r.data.hosts || []); setTotal(r.data.total || 0); }
  }, [page, applied, tab]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    setStats(null);
    (async () => { const r = await adminService.getHostStats(days); if (!cancelled && r.success) setStats(r.data); })();
    return () => { cancelled = true; };
  }, [days]);

  const loadDetail = useCallback(async (id) => {
    setDetails((d) => ({ ...d, [id]: 'loading' }));
    const r = await adminService.getHostDetail(id);
    setDetails((d) => ({ ...d, [id]: r.success ? r.data : 'error' }));
  }, []);
  const toggle = (h) => {
    const next = !open[h.id];
    setOpen((o) => ({ ...o, [h.id]: next }));
    if (next && !details[h.id]) loadDetail(h.id);
  };
  /** After a write: the row, the tiles and any open dropdown all say the new thing. */
  const refreshAll = async (id) => {
    await load();
    const r = await adminService.getHostStats(days); if (r.success) setStats(r.data);
    if (id && open[id]) loadDetail(id);
  };

  const submitSearch = () => { const next = new URLSearchParams(params); if (search) next.set('search', search); else next.delete('search'); setParams(next, { replace: true }); setPage(1); };
  const setTab = (key) => { const next = new URLSearchParams(params); if (key === 'all') next.delete('tab'); else next.set('tab', key); setParams(next, { replace: true }); setPage(1); };

  const noun = { all: 'people who have published an event', live: 'hosts with an event on sale right now', holding: 'hosts whose wallet holds escrow', instant: 'hosts on instant escrow', custom: 'hosts with their own release delay' }[tab] || 'hosts';
  const meta = canRelease ? `${total.toLocaleString()} ${noun}. Settings and RELEASE NOW are yours.`
    : canEdit ? `${total.toLocaleString()} ${noun}. You can change escrow settings; releasing is not yours.`
      : `${total.toLocaleString()} ${noun}.`;

  const tiles = stats ? [
    ['HOSTS', fmt(stats.hosts), 'Published at least one event', 'hosts'],
    ['HOSTING NOW', fmt(stats.hostingNow), `${fmt(stats.liveEvents)} ${stats.liveEvents === 1 ? 'event' : 'events'} on sale`, 'hostingNow'],
    ['NEW HOSTS', fmt(stats.newHosts), `First event in the last ${days} days`, 'newHosts'],
    ['IN ESCROW', money(stats.escrowHeldCents), 'Across every host wallet, right now', 'escrowHeld'],
    ['TICKET MONEY', money(stats.ticketCentsInRange), `Into host wallets, ${days} days`, 'ticketMoney'],
    ['INSTANT ESCROW', fmt(stats.instantEscrowHosts), `${fmt(stats.customDelayHosts)} with their own delay`, 'instant'],
  ] : [];
  const tipFor = (key, title, desc) => (stats?.sources?.[key] ? <AdminTooltip title={title} desc={desc} source={stats.sources[key]} /> : undefined);

  const seriesPoints = useMemo(() => (stats?.series || []).map((d) => ({ date: d.date, newHosts: d.newHosts, cents: d.ticketCents })), [stats]);
  const topHosts = useMemo(() => (stats?.topHosts || []).map((t) => ({
    label: t.label, count: Number(t.value) || 0, tip: `${t.display} of ticket money in the range`, source: stats.topHostsSource,
    onSelect: canSeePeople && t.href ? () => { const m = /person=([^&]+)/.exec(t.href); if (m) openPerson(m[1]); } : undefined,
  })), [stats, canSeePeople, openPerson]);

  return (
    <>
      <AdminPageHeader section="hosts" meta={meta} />
      <div className="flex flex-col gap-4">
        {!stats ? <Card><Loading /></Card> : (
          <>
            <TileGrid label="HOSTS ONLY" min="180px">
              {tiles.map(([label, value, sub, key]) => <StatTile key={label} label={label} value={value} sub={sub} tip={tipFor(key, label, sub)} />)}
            </TileGrid>
            <div className="flex flex-wrap items-center gap-2">
              <SegTrack ariaLabel="Range" value={days} onChange={setDays} options={RANGES.map((d) => ({ value: d, label: `${d}D` }))} />
              <span className="font-mono text-[11px] text-[#6B7280]">NEW HOSTS, TICKET MONEY and the chart follow this range. The other tiles are right now.</span>
            </div>
            <Card title={`TICKET MONEY · ${days} DAYS`} right={`${money(stats.ticketCentsInRange)} INTO HOST WALLETS`} bodyClass="px-5 pb-3">
              <LineChart
                label="Ticket money into host wallets over time"
                points={seriesPoints}
                series={[{ key: 'cents', label: 'TICKET MONEY', format: money }]}
                format={moneyAxis}
                source={stats.sources?.ticketMoney}
                axisInfo={{ title: 'TICKET MONEY', kicker: 'PER DAY', desc: 'Ticket credits that landed in host wallets that day, net of pullbacks. Every cent of it belongs to a host; this is what the hosts page is about.', source: stats.sources?.ticketMoney }}
                foot="A day with no money had no tickets sold, not no hosts."
              />
            </Card>
            <div className="grid gap-[14px]" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
              <Card title={`NEW HOSTS · ${days} DAYS`} right={`${fmt(stats.newHosts)} IN THE RANGE`} bodyClass="px-3 pb-3">
                <ColumnChart
                  height={180}
                  items={seriesPoints.map((d) => ({ key: d.date, label: shortDay(d.date), value: d.newHosts, tip: [['NEW HOSTS', fmt(d.newHosts)]], source: stats.sources?.newHosts }))}
                  axisInfo={{ title: 'NEW HOSTS', kicker: 'PER DAY', desc: 'People whose first published event was made that day. Drafts do not count; publishing does.', source: stats.sources?.newHosts }}
                />
              </Card>
              <Card title={`TOP HOSTS · ${days} DAYS`} right={canSeePeople ? 'A ROW OPENS THE PERSON' : null} bodyClass="px-3 pb-3">
                <BarList items={topHosts} format={money} drill={canSeePeople ? 'OPEN THE PERSON →' : undefined} />
              </Card>
            </div>
          </>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <SubTabs
            tabs={[
              { key: 'all', label: 'ALL' }, { key: 'live', label: 'HOSTING NOW' }, { key: 'holding', label: 'HOLDING ESCROW' },
              { key: 'instant', label: 'INSTANT' }, { key: 'custom', label: 'OWN DELAY' },
            ]}
            active={tab}
            onChange={setTab}
          />
          <SearchField value={search} onChange={setSearch} onSubmit={submitSearch} placeholder="SEARCH EMAIL, NAME OR HANDLE" />
          <Btn variant="solid" size="card" onClick={submitSearch}>SEARCH</Btn>
        </div>

        <DataTable
          columns={[
            { key: 'host', label: 'HOST', fr: 2.2, card: 'title', render: (h) => (
              <span className="flex min-w-0 items-center gap-3">
                <Avatar url={h.avatarUrl} name={h.displayName || h.email} size={36} />
                <span className="flex min-w-0 flex-col">
                  <span className="truncate font-mono text-[13px] font-bold">{h.displayName || h.email}{h.isTestAccount ? <span className="ml-2 text-[10px]">DEMO</span> : null}</span>
                  <span className="truncate font-mono text-[10px] text-[#6B7280]">{h.handle ? `@${h.handle} · ` : ''}{h.email}</span>
                </span>
              </span>
            ) },
            { key: 'eventsHosted', label: 'EVENTS', fr: 1.2, render: (h) => `${fmt(h.eventsHosted)}${h.eventsLive ? ` · ${fmt(h.eventsLive)} LIVE` : ''}` },
            { key: 'ticketsSold', label: 'TICKETS', fr: 0.7, right: true, render: (h) => fmt(h.ticketsSold) },
            { key: 'grossCents', label: 'GROSS', fr: 0.9, right: true, render: (h) => money(h.grossCents) },
            { key: 'walletBalance', label: 'BALANCE', fr: 0.9, right: true, render: (h) => money(h.walletBalance) },
            { key: 'escrowBalance', label: 'ESCROW', fr: 0.9, right: true, render: (h) => money(h.escrowBalance) },
            { key: 'release', label: 'RELEASE', fr: 0.8, card: 'badge', render: (h) => <Pill tone={releaseTone(h)}>{releaseWord(h)}</Pill> },
            { key: 'payoutsReady', label: 'PAYOUTS', fr: 0.8, render: (h) => (h.payoutsReady ? 'READY' : 'NOT YET') },
          ]}
          rows={rows}
          loading={loading}
          onRowClick={canSeePeople ? (h) => openPerson(h.id) : undefined}
          hasActions
          actionsMenu
          actions={(h) => [
            { label: open[h.id] ? 'CLOSE' : 'DETAILS', icon: open[h.id] ? 'close' : 'details', onClick: () => toggle(h) },
            canEdit && { label: 'ESCROW SETTINGS', icon: 'settings', onClick: () => setSettingsFor(h) },
            canRelease && (h.escrowBalance > 0 || h.heldByLedgerCents > 0) && { label: 'RELEASE NOW', icon: 'release', variant: 'magenta', onClick: () => setReleaseFor(h) },
            { divider: true },
            canAdjust && { label: 'ADJUST WALLET', icon: 'wallet', onClick: () => setWalletFor({ person: walletPerson(h), mode: 'adjust' }) },
            isSuperadmin && { label: 'TRANSFER WALLET', icon: 'transfer', variant: 'magenta', onClick: () => setWalletFor({ person: walletPerson(h), mode: 'transfer' }) },
          ].filter(Boolean)}
          expanded={(h) => (open[h.id] ? <HostDropdown host={h} detail={details[h.id]} onRetry={() => loadDetail(h.id)} /> : null)}
          total={total}
          page={page}
          pageSize={20}
          onPage={setPage}
          empty="NOBODY HAS PUBLISHED AN EVENT YET."
        />
      </div>

      <EscrowSettingsModal host={settingsFor} onClose={() => setSettingsFor(null)} onSaved={(id) => { setSettingsFor(null); refreshAll(id); }} />
      <WalletMoveModal person={walletFor?.person} initialMode={walletFor?.mode} onClose={() => setWalletFor(null)} onDone={(id, toId) => { refreshAll(id); if (toId && open[toId]) loadDetail(toId); }} />
      <ReleaseNowModal host={releaseFor} detail={releaseFor ? details[releaseFor.id] : null} onClose={() => setReleaseFor(null)} onDone={(id) => refreshAll(id)} />
      <AdminModal open={!!drill} onClose={() => setDrill(null)} title={drill?.title} kicker={drill?.kicker} width={560}>
        {drill && <DrillList items={drill.items} onPick={(it) => { const m = /person=([^&]+)/.exec(it.href || ''); if (m && canSeePeople) { setDrill(null); openPerson(m[1]); } }} />}
      </AdminModal>
    </>
  );
}

/** Who gets what when an event's money is released: the split by the roster as it stands now. */
export function ShareLine({ shares, className = '' }) {
  if (!shares || shares.length === 0) return null;
  return (
    <span className={`flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] text-[#6B7280] ${className}`} data-testid="share-line">
      <span className="font-bold uppercase tracking-[0.12em] text-[#9CA3AF]">AT RELEASE</span>
      {shares.map((sh) => (
        <span key={sh.userId} className="whitespace-nowrap">
          <span className="font-bold text-black">{sh.handle ? `@${sh.handle}` : sh.name || 'SOMEONE'}</span>
          {' '}{money(sh.shareCents)}
          <span className="text-[#9CA3AF]"> · {sh.splitLabel}</span>
        </span>
      ))}
    </span>
  );
}

/** The row's dropdown: the wallet, the hosting record, what is held and why, the last releases. */
export function HostDropdown({ host, detail, onRetry }) {
  if (!detail || detail === 'loading') return <div className="py-2"><Loading /></div>;
  if (detail === 'error') return <div className="flex flex-wrap items-center gap-3 py-1 font-mono text-[11px] text-[#DC2626]">COULD NOT LOAD THIS HOST. <Btn size="row" onClick={onRetry}>TRY AGAIN</Btn></div>;
  const d = detail;
  const tile = (label, value, cls = '') => (
    <div key={label} className="rounded-[14px] bg-white px-3 py-[10px]">
      <div className="font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-[#9CA3AF]">{label}</div>
      <div className={`font-mono text-[13px] font-bold ${cls}`}>{value}</div>
    </div>
  );
  return (
    <div className="flex flex-col gap-3" data-testid={`host-dropdown-${host.id}`}>
      <div>
        <FieldLabel className="pb-2 pl-1">WALLET</FieldLabel>
        <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}>
          {tile('BALANCE', money(d.balance))}
          {tile('ESCROW', money(d.escrowBalance))}
          {tile('HELD FOR EVENTS', money(d.heldByLedgerCents))}
          {d.otherEscrowCents > 0 && tile('OTHER ESCROW', money(d.otherEscrowCents))}
          {tile('EARNED', money(d.totalEarned), 'text-[#15803D]')}
          {tile('WITHDRAWN', `${money(d.withdrawnCents)} · ${fmt(d.withdrawalCount)}`)}
          {tile('PAYOUTS', d.payoutsReady ? 'READY' : 'NOT SET UP', d.payoutsReady ? 'text-[#15803D]' : 'text-[#DC2626]')}
          {tile('RELEASE', releaseWord(d))}
        </div>
      </div>
      <div>
        <FieldLabel className="pb-2 pl-1">HOSTING</FieldLabel>
        <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}>
          {tile('EVENTS', `${fmt(d.eventsHosted)} · ${fmt(d.eventsLive)} LIVE`)}
          {tile('ENDED / CANCELED', `${fmt(d.eventsEnded)} / ${fmt(d.eventsCanceled)}`)}
          {tile('TICKETS SOLD', fmt(d.ticketsSold))}
          {tile('GUESTS', fmt(d.distinctGuests))}
          {tile('GROSS', money(d.grossCents))}
          {tile('REFUNDED', money(d.refundedCents), d.refundedCents > 0 ? 'text-[#DC2626]' : '')}
          {tile('RELEASED', money(d.releasedCents))}
          {tile('AVG TICKET', d.ticketsSold > 0 ? money(Math.round(d.grossCents / d.ticketsSold)) : '—')}
          {tile('FIRST EVENT', d.firstEventAt ? shortDate(d.firstEventAt) : '—')}
          {tile('LAST EVENT', d.lastEventAt ? shortDate(d.lastEventAt) : '—')}
        </div>
      </div>
      <div>
        <FieldLabel className="pb-2 pl-1">HELD RIGHT NOW · {d.held.length === 0 ? 'NOTHING' : `${d.held.length} ${d.held.length === 1 ? 'EVENT' : 'EVENTS'}`}</FieldLabel>
        {d.held.length > 0 && (
          <div className="flex flex-col gap-1">
            {d.held.map((e) => (
              <div key={e.eventId} className="flex flex-wrap items-center gap-3 rounded-[14px] bg-white px-3 py-2 font-mono text-[11px]">
                <span className="min-w-0 flex-1 truncate font-bold">{e.title}</span>
                <Pill tone={statusTone(e.status)}>{statusWord(e.status)}</Pill>
                <span className="text-[#6B7280]">{shortDate(e.dateTime)}</span>
                <span className="text-[#6B7280]">{e.escrowReleaseAt ? `RELEASES ${shortDate(e.escrowReleaseAt)}` : LIVE.has(e.status) ? 'AFTER THE EVENT' : 'ON THE NEXT SWEEP'}</span>
                <span className="font-bold">{money(e.heldCents)}</span>
                {e.shares && e.shares.length > 1 && <ShareLine shares={e.shares} className="basis-full" />}
              </div>
            ))}
          </div>
        )}
      </div>
      {d.recentReleases.length > 0 && (
        <div>
          <FieldLabel className="pb-2 pl-1">LAST RELEASES</FieldLabel>
          <div className="flex flex-col gap-1">
            {d.recentReleases.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-3 rounded-[14px] bg-white px-3 py-2 font-mono text-[11px]">
                <span className="text-[#6B7280]">{shortDate(r.createdAt)}</span>
                <span className="min-w-0 flex-1 truncate">{r.eventTitle || r.description || r.type}</span>
                <span className="font-bold text-[#15803D]">+{money(r.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** ESCROW SETTINGS — INSTANT ESCROW and the RELEASE DELAY, on the panel's modal. */
export function EscrowSettingsModal({ host, onClose, onSaved }) {
  const [instant, setInstant] = useState(false);
  const [hours, setHours] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (host) { setInstant(!!host.instantEscrow); setHours(host.escrowReleaseHours == null ? '' : String(host.escrowReleaseHours)); setError(''); } }, [host]);
  const save = async () => {
    const trimmed = hours.trim();
    let h = null;
    if (trimmed !== '') {
      h = Number(trimmed);
      if (!Number.isInteger(h) || h < 0 || h > 720) { setError('Whole hours from 0 to 720, or empty for the default 24.'); return; }
    }
    setSaving(true);
    const r = await adminService.setHostEscrowSettings(host.id, { instantEscrow: instant, escrowReleaseHours: h });
    setSaving(false);
    if (r.success) onSaved(host.id); else setError(r.error || 'Could not save.');
  };
  if (!host) return null;
  return (
    <AdminModal
      open
      onClose={onClose}
      title="ESCROW SETTINGS"
      kicker={`How ${host.displayName || host.email}'s ticket money reaches their balance.`}
      footer={<><Btn size="sheet" onClick={onClose}>CANCEL</Btn><Btn size="sheet" variant="solid" onClick={save} disabled={saving}>{saving ? 'SAVING…' : 'SAVE SETTINGS'}</Btn></>}
    >
      <div className="flex flex-col gap-4">
        <div className="rounded-[18px] bg-[#F7F7F7] p-4">
          <CheckBox checked={instant} onChange={setInstant} label="INSTANT ESCROW" />
          <p className="m-0 pt-2 font-mono text-[11px] leading-[1.6] text-[#6B7280]">Every ticket goes straight into the balance as it is sold. Nothing waits for the event to end. A refund after that comes out of the balance.</p>
        </div>
        <Field label="RELEASE DELAY · HOURS AFTER THE EVENT ENDS" error={error}>
          <input type="number" min="0" max="720" step="1" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="24" className={inputClass} aria-label="Release delay in hours" disabled={instant} />
        </Field>
        <FieldLabel>{instant ? 'NOT USED WHILE INSTANT ESCROW IS ON.' : 'EMPTY IS THE DEFAULT 24. 0 RELEASES ON THE FIRST SWEEP AFTER THE EVENT ENDS.'}</FieldLabel>
      </div>
    </AdminModal>
  );
}

const VERDICT = {
  delivered: ['DELIVERED', 'good'],
  partial: ['PARTLY DELIVERED', 'warn'],
  nothing_held: ['NOTHING HELD', 'neutral'],
  failed: ['NOT DELIVERED', 'bad'],
};
const STEP_MARK = { done: ['✓', '#15803D'], skipped: ['–', '#9CA3AF'], failed: ['✕', '#DC2626'] };

/** RELEASE NOW — a confirm, then the step log of what moved and whether it landed. */
/** Has this event happened yet? Live statuses and a start in the future both say no. */
const notYet = (e) => LIVE.has(e.status) || (e.dateTime && new Date(e.dateTime).getTime() > Date.now());

/**
 * RELEASE NOW — per event (Oct 26 2026). The confirm lists every event this wallet holds money
 * for, with a box each. Events that are over are ticked; one that has not happened yet is not,
 * and says so, because releasing a ticket's money before the party is the one thing the hold
 * exists to prevent. Staff can still tick it — on purpose, and it goes in the log.
 */
export function ReleaseNowModal({ host, detail: given, onClose, onDone }) {
  const [phase, setPhase] = useState('confirm'); // confirm | running | done
  const [detail, setDetail] = useState(given || null);
  const [picked, setPicked] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!host) return;
    setPhase('confirm'); setResult(null); setError(''); setPicked(null); setDetail(given || null);
    if (!given) (async () => { const r = await adminService.getHostDetail(host.id); if (r.success) setDetail(r.data); else setError(r.error || 'Could not read what is held.'); })();
  }, [host, given]);
  const held = detail?.held || [];
  const chosen = picked ?? new Set(held.filter((e) => !notYet(e)).map((e) => e.eventId));
  const toggle = (id) => setPicked((prev) => { const n = new Set(prev ?? chosen); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const pickedCents = held.filter((e) => chosen.has(e.eventId)).reduce((s, e) => s + (e.poolCents || e.heldCents), 0);
  const run = async () => {
    setPhase('running');
    const r = await adminService.releaseHostEscrow(host.id, [...chosen]);
    if (r.success) { setResult(r.data); setPhase('done'); onDone?.(host.id); }
    else { setError(r.error || 'The release did not run.'); setPhase('done'); }
  };
  if (!host) return null;
  const who = host.displayName || host.email;
  const [verdictLabel, verdictTone] = VERDICT[result?.verdict] || VERDICT.failed;
  return (
    <AdminModal
      open
      onClose={phase === 'running' ? () => {} : onClose}
      title="RELEASE ESCROW NOW"
      kicker={phase === 'confirm'
        ? `Pick the events to settle now, ahead of the usual window. Each one's whole take is divided by its roster as it stands today — the host and every co-host get their share into their balance. Pro bookings and dispute freezes stay put.`
        : `${who} · ${money(result?.heldBeforeCents ?? pickedCents)} picked when you pressed it.`}
      footer={phase === 'confirm'
        ? <><Btn size="sheet" onClick={onClose}>CANCEL</Btn><Btn size="sheet" variant="solid" onClick={run} disabled={!detail || chosen.size === 0}>{chosen.size === 0 ? 'PICK AN EVENT' : `RELEASE ${money(pickedCents)}`}</Btn></>
        : <Btn size="sheet" variant="solid" onClick={onClose} disabled={phase === 'running'}>{phase === 'running' ? 'RUNNING…' : 'DONE'}</Btn>}
      width={640}
    >
      {phase === 'confirm' && (
        <div className="flex flex-col gap-3">
          {!detail && !error && <Loading />}
          {error && <p className="m-0 font-mono text-[12px] text-[#DC2626]">{error}</p>}
          {detail && held.length === 0 && <EmptyLine>NO EVENT HOLDS MONEY FOR THIS WALLET.{detail.otherEscrowCents > 0 ? ` THE ${money(detail.otherEscrowCents)} IN ESCROW IS A PRO BOOKING OR A DISPUTE FREEZE.` : ''}</EmptyLine>}
          {held.length > 0 && (
            <div className="flex flex-col gap-1" role="group" aria-label="Events to release">
              {held.map((e) => {
                const soon = notYet(e);
                return (
                  <div key={e.eventId} className={`flex flex-wrap items-center gap-3 rounded-[14px] px-3 py-2 font-mono text-[11px] ${chosen.has(e.eventId) ? 'bg-[#F7F7F7]' : 'bg-white ring-1 ring-[#F2F2F2]'}`} data-testid={`release-event-${e.eventId}`}>
                    <CheckBox checked={chosen.has(e.eventId)} onChange={() => toggle(e.eventId)} label="" size={24} />
                    <span className="min-w-0 flex-1 truncate font-bold">{e.title}</span>
                    {soon ? <Pill tone="warn">HASN'T HAPPENED YET</Pill> : <Pill tone={statusTone(e.status)}>{statusWord(e.status)}</Pill>}
                    <span className="text-[#6B7280]">{shortDate(e.dateTime)}</span>
                    <span className="font-bold">{money(e.poolCents || e.heldCents)}</span>
                    {e.shares && e.shares.length > 0 && <ShareLine shares={e.shares} className="basis-full pl-9" />}
                  </div>
                );
              })}
            </div>
          )}
          {held.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 px-1">
              <span className="font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-[#6B7280]">{chosen.size} OF {held.length} PICKED · {money(pickedCents)} OF {money(held.reduce((s, e) => s + (e.poolCents || e.heldCents), 0))}</span>
              <span className="flex gap-2">
                <Btn onClick={() => setPicked(new Set(held.filter((e) => !notYet(e)).map((e) => e.eventId)))} className="px-[11px] py-[7px]">ONLY WHAT'S OVER</Btn>
                <Btn onClick={() => setPicked(new Set(held.map((e) => e.eventId)))} className="px-[11px] py-[7px]">EVERYTHING</Btn>
              </span>
            </div>
          )}
          <FieldLabel>THIS WRITES TO THE LEDGER AND NOTIFIES THE HOST. IT CANNOT BE UNDONE FROM HERE.</FieldLabel>
        </div>
      )}
      {phase === 'running' && (
        <div className="flex flex-col gap-2" role="status" aria-live="polite">
          <StepLine mark={['…', '#000']} label="FIND THE WALLET" detail="Locking the wallet and reading the ledger." />
          <StepLine mark={['', '#9CA3AF']} label={`RELEASE ${chosen.size} ${chosen.size === 1 ? 'EVENT' : 'EVENTS'}`} detail="Waiting." muted />
          <StepLine mark={['', '#9CA3AF']} label="CHECK THE WALLET" detail="Waiting." muted />
        </div>
      )}
      {phase === 'done' && error && <p className="m-0 font-mono text-[12px] text-[#DC2626]">{error}</p>}
      {phase === 'done' && result && (
        <div className="flex flex-col gap-3" role="status" aria-live="polite">
          <div className="flex flex-wrap items-center gap-3 rounded-[18px] bg-[#F7F7F7] px-4 py-3">
            <Pill tone={verdictTone} data-testid="release-verdict">{verdictLabel}</Pill>
            <span className="font-mono text-[12px] font-bold">{result.summary}</span>
          </div>
          <div className="flex flex-col gap-2">
            {result.steps.map((s) => <StepLine key={s.key} mark={STEP_MARK[s.status] || STEP_MARK.done} label={s.label} detail={s.detail} failed={s.status === 'failed'} extra={s.shares && s.shares.length > 0 ? <ShareLine shares={s.shares} /> : null} />)}
          </div>
          <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))' }}>
            {[['MOVED', money(result.movedCents)], ['BALANCE', `${money(result.balanceBefore)} → ${money(result.balanceAfter)}`], ['ESCROW', `${money(result.escrowBefore)} → ${money(result.escrowAfter)}`], ['EVENTS', fmt(result.eventsReleased)], ...(result.eventsLeft > 0 ? [['STILL HELD', `${money(result.leftHeldCents)} · ${fmt(result.eventsLeft)} ${result.eventsLeft === 1 ? 'EVENT' : 'EVENTS'}`]] : [])].map(([l, v]) => (
              <div key={l} className="rounded-[14px] bg-[#F7F7F7] px-3 py-[10px]">
                <div className="font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-[#9CA3AF]">{l}</div>
                <div className="font-mono text-[12px] font-bold">{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </AdminModal>
  );
}

function StepLine({ mark, label, detail, failed = false, muted = false, extra = null }) {
  const [glyph, color] = mark;
  return (
    <div className={`flex items-start gap-3 rounded-[14px] px-3 py-2 ${failed ? 'bg-[#FEE2E2]' : 'bg-[#F7F7F7]'} ${muted ? 'opacity-50' : ''}`}>
      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-white font-mono text-[12px] font-bold" style={{ color }}>{glyph}</span>
      <span className="flex min-w-0 flex-col gap-1">
        <span className="font-mono text-[10px] font-bold uppercase tracking-[0.14em]">{label}</span>
        {detail && <span className={`font-mono text-[11px] leading-[1.5] ${failed ? 'text-[#B91C1C]' : 'text-[#6B7280]'}`}>{detail}</span>}
        {extra}
      </span>
    </div>
  );
}
